import { useCallback } from "react";
import { useAtomValue } from "jotai";
import { useOrganizationApi } from "@/hooks/apiHooks/useOrganizationApi";
import { useMountRead } from "@/hooks/useMountRead";
import { organizationsAtom } from "@/store/organizationAtoms";
import { isOrganizationAdmin } from "./isOrganizationAdmin";

// The user's organization and whether they are its Admin, for a wall that asks
// only the Admin for a card (#368, #445). organizations/me is read once on
// mount. `readStatus` is that read's own status (#407): a caller with an
// organization already in hand keeps showing it while the read is loading or
// after it failed.
export const useOrganizationRole = (userId: string) => {
    const { getMeOrganizations } = useOrganizationApi();
    const organizations = useAtomValue(organizationsAtom);
    // `silent`: the callers render the failure inline with their own Retry, so
    // useApi's toast would announce the same failure twice. Wrapped so the
    // `isCurrent` useMountRead hands a read never reaches getMeOrganizations as
    // its options.
    const readOrganizations = useCallback(
        () => getMeOrganizations({ silent: true }),
        [getMeOrganizations],
    );
    const { readStatus, reload } = useMountRead(readOrganizations);

    // A user belongs to exactly one organization; the endpoint returns an array.
    const organization = organizations[0];

    return {
        readStatus,
        reload,
        organization,
        isAdmin: isOrganizationAdmin(organization, userId),
    };
};
